import { useTranslation } from "@sagot/i18n";
import { Button, cn, EmptyState, ErrorState, Icon, Skeleton, toast } from "@sagot/ui";
import { useErrorMessage } from "../../../lib/api-errors";
import { Composer } from "./composer";
import { DetailsPanel } from "./details-panel";
import { useConversationActions, useConversationDetail } from "./inbox-api";
import { useInboxStore } from "./inbox-store";
import { ThreadHeader } from "./thread-header";
import { ThreadMessages } from "./thread-messages";
import { useInboxPermissions } from "./use-inbox-permissions";
import { useNow } from "./use-now";

function ThreadSkeleton() {
  return (
    <div className="flex min-h-0 flex-1 flex-col" aria-hidden="true">
      <div className="flex items-center gap-3 border-b border-line bg-surface px-6 py-3">
        <Skeleton className="size-9 rounded-pill" />
        <div className="space-y-2">
          <Skeleton className="h-4 w-[180px]" />
          <Skeleton className="h-3 w-[260px]" />
        </div>
      </div>
      <div className="flex-1 space-y-4 px-8 py-5">
        <Skeleton className="h-16 w-[60%]" />
        <Skeleton className="ml-auto h-24 w-[70%]" />
        <Skeleton className="h-12 w-[45%]" />
      </div>
      <Skeleton className="mx-6 mb-4 h-28" />
    </div>
  );
}

interface ThreadPaneProps {
  conversationId: string | null;
  onBack: () => void;
  className?: string;
}

/** Center + right panes for the open conversation (thread, composer, details). */
export function ThreadPane({ conversationId, onBack, className }: ThreadPaneProps) {
  const { t } = useTranslation();
  const detail = useConversationDetail(conversationId);
  const actions = useConversationActions(conversationId ?? "");
  const permissions = useInboxPermissions(detail.data?.conversation.agent_id ?? null);
  const errorMessage = useErrorMessage();
  const pinned = useInboxStore((s) => s.detailsPinned);
  const overlay = useInboxStore((s) => s.detailsOverlay);
  const toggleDetails = useInboxStore((s) => s.toggleDetails);
  const closeDetails = useInboxStore((s) => s.closeDetails);
  const now = useNow();

  if (!conversationId) {
    return (
      <section className={cn("flex min-w-0 flex-1 flex-col items-center justify-center bg-canvas", className)}>
        <EmptyState icon="messages-square" title={t("engage.inbox.thread.selectTitle")} description={t("engage.inbox.thread.selectBody")} />
      </section>
    );
  }
  if (detail.isPending) return <section className={cn("flex min-w-0 flex-1 flex-col bg-canvas", className)}><ThreadSkeleton /></section>;
  if (detail.isError) {
    return (
      <section className={cn("flex min-w-0 flex-1 flex-col items-center justify-center bg-canvas", className)}>
        <ErrorState title={t("engage.inbox.thread.loadError")} onRetry={() => void detail.refetch()} retryLabel={t("common.retry")} />
        <Button variant="ghost" className="lg:hidden" onClick={onBack}>
          <Icon name="arrow-left" />
          {t("engage.inbox.thread.back")}
        </Button>
      </section>
    );
  }

  const data = detail.data;
  const paused = data.conversation.ai_paused;
  const ensureTakeover = async () => {
    if (paused) return;
    await actions.takeover.mutateAsync();
    toast.info(t("engage.inbox.thread.tookOver"));
  };
  const handBack = () => {
    actions.release.mutateAsync().then(() => toast.info(t("engage.inbox.thread.returned")), (error: unknown) => toast.error(errorMessage(error)));
  };
  const toggleTakeover = () => {
    const pending = paused ? actions.release.mutateAsync().then(() => toast.info(t("engage.inbox.thread.returned"))) : ensureTakeover();
    pending.catch((error: unknown) => toast.error(errorMessage(error)));
  };

  return (
    <>
      <section aria-labelledby="thread-heading" className={cn("flex min-h-0 min-w-0 flex-1 flex-col bg-canvas", className)}>
        <ThreadHeader detail={data} actions={actions} permissions={permissions} onBack={onBack} onToggleDetails={toggleDetails} onTakeoverToggle={toggleTakeover} onHandBack={handBack} />
        <ThreadMessages detail={data} />
        <Composer conversation={data.conversation} actions={actions} permissions={permissions} ensureTakeover={() => ensureTakeover().catch((error: unknown) => { toast.error(errorMessage(error)); })} />
      </section>
      <aside
        aria-label={t("engage.inbox.details.title")}
        className={cn(
          "flex min-h-0 w-80 shrink-0 flex-col border-l border-line bg-surface max-xl:fixed max-xl:bottom-0 max-xl:right-0 max-xl:top-14 max-xl:z-30 max-xl:shadow-pop max-sm:w-full",
          pinned ? "xl:flex" : "xl:hidden",
          overlay ? "max-xl:flex" : "max-xl:hidden",
        )}
        data-testid="details-panel"
      >
        <div className="flex h-12 shrink-0 items-center justify-between border-b border-line px-4">
          <span className="text-sm font-bold">{t("engage.inbox.details.title")}</span>
          <Button variant="ghost" size="sm" iconOnly aria-label={t("engage.inbox.details.close")} onClick={closeDetails}>
            <Icon name="x" />
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto">
          <DetailsPanel detail={data} actions={actions} permissions={permissions} now={now} />
        </div>
      </aside>
    </>
  );
}
